import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl, API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  ChevronLeft as ChevronLeftIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import MainLayout from '../../layout/MainLayout';
import DateUtils from '../../utils/dateUtils';

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [completedRequests, setCompletedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markdownContent, setMarkdownContent] = useState(null);
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [availablePlots, setAvailablePlots] = useState([]);
  const [reportMetadata, setReportMetadata] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableFiles, setAvailableFiles] = useState([]);

  const navigate = useNavigate();
  const params = useParams();
  const requestId = params.id;

  // Helper function to build authenticated URLs for iframes
  const buildAuthenticatedUrl = (apiPath) => {
    const token = localStorage.getItem('authToken');
    let url;
    if (apiPath.startsWith('http://') || apiPath.startsWith('https://')) {
      // Already a full URL (e.g. from API_BASE_URL)
      url = apiPath;
    } else if (apiPath.startsWith('/api/')) {
      // Relative path with /api/ prefix — strip it since API_BASE_URL includes /api
      url = `${API_BASE_URL}${apiPath.slice(4)}`;
    } else {
      url = `${API_BASE_URL}${apiPath}`;
    }
    // Add token as query parameter for iframe authentication
    return token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
  };

  // Laden aller abgeschlossenen Anfragen
  useEffect(() => {
    const fetchCompletedRequests = async () => {
      setLoading(true);
      try {
        console.log('=== CLIENT REPORTS API DEBUG ===');
        console.log('CLIENT_ORDERS.LIST:', buildApiUrl(API_ENDPOINTS.CLIENT_ORDERS.LIST));

        const response = await axios.get(buildApiUrl(API_ENDPOINTS.CLIENT_ORDERS.LIST));
        console.log('Orders response:', response.data);

        // Handle pagination format {count, results} or direct array
        const ordersList = response.data.results || response.data;

        // IDs of orders that have demo reports available
        const ordersWithReports = [17]; // Only ID 17 has demo-001 report

        // Nur abgeschlossene Anfragen filtern UND nur die mit verfügbaren Reports
        const completed = ordersList.filter(req =>
          (req.status === 'completed' || req.status === 'delivered') &&
          ordersWithReports.includes(req.id)
        );
        
        console.log(`${completed.length} abgeschlossene Anfragen gefunden`);
        setCompletedRequests(completed);
        
        // Wenn eine ID in der URL ist, diese Anfrage auswählen
        if (requestId) {
          const selected = completed.find(req => req.id == requestId || req.id === parseInt(requestId));
          if (selected) {
            setSelectedRequest(selected);
            loadReportData(selected);
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der abgeschlossenen Anfragen:', error);
        setError('Die Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedRequests();
  }, [requestId]);

  // Content aus Provider Report generieren
  const generateContentFromProviderReport = (providerReport, requestTitle) => {
    const reportData = {
      content: [
        {
          type: 'heading1',
          title: `Studienbericht: ${requestTitle || 'Provider Report'}`,
          content: [
            `Report ID: ${providerReport.id}`,
            `Generiert am: ${new Date(providerReport.generated_at).toLocaleDateString('de-DE')}`,
            '',
            '**Verfügbare Dateien:**',
            ...(providerReport.files || []).map(file =>
              `• ${file.file_name} (${Math.round(file.file_size / 1024)} KB)`
            )
          ],
          plots: []
        }
      ],
      availablePlots: (providerReport.files || [])
        .filter(f => f.file_type === 'html')
        .map(f => ({
          filename: f.file_name,
          url: `${API_BASE_URL}/hermes/reports/${providerReport.id}/direct/download/${f.relative_path}`,
          type: f.file_type
        }))
    };

    return reportData;
  };

  // Mock-Dateien für verschiedene Studien generieren (wie in Provider App)
  const generateMockFiles = (reportId) => {
    const mockFiles = {
      1: [ // SGARI-Studie
        {
          id: 'mock-1',
          name: 'SGARI-Studie Hauptbericht',
          type: 'pdf',
          size: '4.8 MB',
          date: '2025-03-15',
          description: 'Vollständiger Analysebericht mit allen Ergebnissen und Methodik.',
          downloadUrl: '#' // Mock URL
        },
        {
          id: 'mock-2',
          name: 'Statistische Auswertung',
          type: 'xlsx',
          size: '3.2 MB',
          date: '2025-03-14',
          description: 'Detaillierte statistische Auswertungen in Tabellenform.',
          downloadUrl: '#' // Mock URL
        },
        {
          id: 'mock-3',
          name: 'Visualisierungen',
          type: 'pptx',
          size: '5.7 MB',
          date: '2025-03-15',
          description: 'Visualisierungen der wichtigsten Ergebnisse als Präsentation.',
          downloadUrl: '#' // Mock URL
        },
        {
          id: 'mock-4',
          name: 'Rohdaten (anonymisiert)',
          type: 'csv',
          size: '9.3 MB',
          date: '2025-03-14',
          description: 'Anonymisierte Rohdaten für weiterführende Analysen.',
          downloadUrl: '#' // Mock URL
        }
      ],
      2: [ // Diabetes-Studie
        {
          id: 'mock-5',
          name: 'Diabetes Typ 2 Hauptbericht',
          type: 'pdf',
          size: '3.1 MB',
          date: '2025-03-10',
          description: 'Vollständiger Analysebericht der Diabetes Typ 2 Studie.',
          downloadUrl: '#'
        }
      ],
      3: [ // IL-23-Inhibitoren Studie
        {
          id: 'mock-6',
          name: 'IL-23-Inhibitoren Hauptbericht',
          type: 'pdf',
          size: '2.9 MB',
          date: '2025-03-12',
          description: 'Vollständiger Analysebericht der IL-23-Inhibitoren Studie.',
          downloadUrl: '#'
        }
      ]
    };

    return mockFiles[reportId] || [];
  };

  // Mock-Daten für Reports generieren
  const generateMockReportData = (reportId, requestTitle) => {
    const mockReports = {
      1: {
        title: 'Wirksamkeit und Sicherheit von Androgenrezeptor-Hemmern der zweiten Generation bei nmCRPC',
        content: [
          {
            type: 'heading1',
            title: 'Studienbericht: Androgenrezeptor-Hemmer bei nmCRPC',
            content: [
              'Diese Studie untersuchte die Wirksamkeit und Sicherheit von Androgenrezeptor-Hemmern der zweiten Generation bei nicht-metastasiertem kastrationsresistentem Prostatakarzinom (nmCRPC).',
              '',
              '**Studiendesign:** Retrospektive Kohortenstudie',
              '**Zeitraum:** Januar 2020 - Dezember 2023',
              '**Patientenanzahl:** 347 Patienten'
            ],
            plots: []
          },
          {
            type: 'heading2',
            title: 'Primäre Endpunkte',
            content: [
              'Die primären Endpunkte der Studie waren:',
              '',
              '• **Progressionsfreies Überleben (PFS):** Median 24.8 Monate (95% CI: 22.1-27.5)',
              '• **Metastasen-freies Überleben (MFS):** Median 31.2 Monate (95% CI: 28.4-34.1)',
              '• **Gesamtüberleben (OS):** Median nicht erreicht (Follow-up: 36 Monate)'
            ],
            plots: []
          },
          {
            type: 'heading2',
            title: 'Sekundäre Endpunkte',
            content: [
              '**Sicherheitsprofil:**',
              '',
              '• Grad 3/4 Nebenwirkungen: 23.4% der Patienten',
              '• Häufigste Nebenwirkungen: Fatigue (67%), Hypertension (34%), Hautausschlag (28%)',
              '• Therapieabbruch aufgrund von Nebenwirkungen: 8.9%',
              '',
              '**Lebensqualität:**',
              '• FACT-P Score: Baseline 127.3 ± 18.7, 12 Monate: 124.8 ± 19.2',
              '• Keine signifikante Verschlechterung der Lebensqualität'
            ],
            plots: []
          },
          {
            type: 'heading2',
            title: 'Schlussfolgerungen',
            content: [
              'Die Ergebnisse zeigen eine signifikante Wirksamkeit der Androgenrezeptor-Hemmer bei nmCRPC-Patienten:',
              '',
              '• **Hohe Wirksamkeit:** PFS und MFS deutlich verlängert im Vergleich zu historischen Kontrollen',
              '• **Akzeptables Sicherheitsprofil:** Nebenwirkungen überwiegend mild bis moderat',
              '• **Erhaltung der Lebensqualität:** Keine signifikante Verschlechterung',
              '',
              '**Empfehlung:** Androgenrezeptor-Hemmer sollten als Standardtherapie für nmCRPC-Patienten in Betracht gezogen werden.'
            ],
            plots: []
          }
        ],
        availablePlots: []
      },
      2: {
        title: 'Diabetes Typ 2 Studie',
        content: [
          {
            type: 'heading1',
            title: 'Diabetes Typ 2 Langzeitstudie',
            content: [
              'Umfassende Analyse der Langzeiteffekte verschiedener Diabetestherapien.',
              '',
              '**Studiendesign:** Prospektive Beobachtungsstudie',
              '**Zeitraum:** 2018 - 2023',
              '**Patientenanzahl:** 1,247 Patienten'
            ],
            plots: []
          }
        ],
        availablePlots: []
      },
      3: {
        title: 'IL-23-Inhibitoren bei Rheumatoider Arthritis',
        content: [
          {
            type: 'heading1',
            title: 'IL-23-Inhibitoren Wirksamkeitsstudie',
            content: [
              'Evaluierung der Wirksamkeit von IL-23-Inhibitoren bei rheumatoider Arthritis.',
              '',
              '**Studiendesign:** Randomisierte kontrollierte Studie',
              '**Zeitraum:** 2021 - 2023',
              '**Patientenanzahl:** 892 Patienten'
            ],
            plots: []
          }
        ],
        availablePlots: []
      }
    };

    return mockReports[reportId] || {
      title: requestTitle || 'Unbekannte Studie',
      content: [
        {
          type: 'heading1',
          title: `Studienbericht: ${requestTitle || 'Unbekannte Studie'}`,
          content: [
            'Für diese Studie sind derzeit keine detaillierten Berichtsdaten verfügbar.',
            '',
            '**Status:** Bericht in Bearbeitung',
            '**Report ID:** ' + reportId
          ],
          plots: []
        }
      ],
      availablePlots: []
    };
  };

  // ID-Mapping zwischen Django Client API und Report API
  const mapClientIdToReportId = (clientId, requestTitle) => {
    // Mapping basierend auf bekannten Studien
    const idMappings = {
      17: 1, // "Wirksamkeit und Sicherheit von Androgenrezeptor-Hemmern der zweiten Generation bei nmCRPC"
      22: 2, // "Diabetes Typ 2 Studie"
      23: 3  // "IL-23-Inhibitoren bei Rheumatoider Arthritis"
    };

    // Titel-basiertes Mapping als Fallback
    const titleMappings = {
      'Wirksamkeit und Sicherheit von Androgenrezeptor-Hemmern der zweiten Generation bei nmCRPC': 1,
      'Diabetes Typ 2 Studie': 2,
      'IL-23-Inhibitoren bei Rheumatoider Arthritis': 3
    };

    // Erst ID-Mapping versuchen
    if (idMappings[clientId]) {
      console.log(`Client ID ${clientId} → Report ID ${idMappings[clientId]} (ID-Mapping)`);
      return idMappings[clientId];
    }

    // Dann Titel-Mapping versuchen
    if (requestTitle && titleMappings[requestTitle]) {
      console.log(`Client ID ${clientId} → Report ID ${titleMappings[requestTitle]} (Titel-Mapping: "${requestTitle}")`);
      return titleMappings[requestTitle];
    }

    // Fallback: Original ID verwenden
    console.log(`Client ID ${clientId} → Report ID ${clientId} (Fallback)`);
    return clientId;
  };

  // Laden der Report-Daten für eine spezifische Anfrage (wie Provider App)
  const loadReportData = async (clientRequest) => {
    if (!clientRequest?.id) {
      console.warn('loadReportData: Keine Anfrage oder ID gefunden');
      setReportMetadata(null);
      setMarkdownContent(null);
      setAvailablePlots([]);
      setAvailableFiles([]);
      return;
    }

    console.log('=== LOADING REPORT DATA ===');
    console.log('Client Request:', clientRequest);

    const reportId = mapClientIdToReportId(clientRequest.id, clientRequest.name);
    console.log(`Client ID ${clientRequest.id} mapped to Report ID ${reportId}`);

    setReportLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');

      const reportsEndpoint = `${API_BASE_URL}/hermes/reports/${reportId}/report-markdown/`;
      const directEndpoint = `${API_BASE_URL}/hermes/reports/${reportId}/direct/`;

      console.log('Using Django API endpoints:');
      console.log(`Reports Markdown: ${reportsEndpoint}`);
      console.log(`Reports Direct: ${directEndpoint}`);

      // Zuerst versuchen: Django Markdown API
      try {
        console.log('Attempting Markdown API for Report...');
        const markdownResponse = await axios.get(reportsEndpoint, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          timeout: 8000
        });

        if (markdownResponse.data && markdownResponse.data.content) {
          console.log('✓ Markdown API successful, content loaded');
          console.log('Markdown content sections:', markdownResponse.data.content.length);
          setMarkdownContent(markdownResponse.data.content);
          setAvailablePlots(markdownResponse.data.availablePlots || []);

          // Lade zusätzlich Metadata für PDF Download
          try {
            const metadataResponse = await axios.get(directEndpoint, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {},
              timeout: 5000
            });

            if (metadataResponse.data && metadataResponse.data.length > 0) {
              const latestReport = metadataResponse.data[0];
              const primaryFile = latestReport.files?.find(f => f.is_primary) || latestReport.files?.[0];

              if (primaryFile) {
                setReportMetadata({
                  filename: primaryFile.file_name,
                  size: `${Math.round(primaryFile.file_size / 1024)} KB`,
                  creationDate: new Date(latestReport.generated_at).toLocaleDateString('de-DE'),
                  reportId: latestReport.id,
                  filePath: primaryFile.relative_path
                });
              }

              // Setze auch hier die verfügbaren Dateien (falls noch nicht gesetzt)
              if (metadataResponse.data.length > 0 && availableFiles.length === 0) {
                const latestReport = metadataResponse.data[0];
                const allFiles = latestReport.files || [];
                const nonPrimaryFiles = allFiles.filter(f => !f.is_primary).map(f => ({
                  id: f.id,
                  name: f.file_name,
                  type: f.file_type,
                  size: `${Math.round(f.file_size / 1024)} KB`,
                  date: new Date(latestReport.generated_at).toLocaleDateString('de-DE'),
                  description: f.file_type === 'html' ? 'Interaktive Visualisierung' :
                              f.file_type === 'pdf' ? 'PDF-Dokument' : 'Datendatei',
                  downloadUrl: `${API_BASE_URL}/hermes/reports/${reportId}/direct/download/${f.relative_path}`
                }));
                setAvailableFiles(nonPrimaryFiles);
              }
            }
          } catch (metaError) {
            console.log('Metadata loading failed, continuing without PDF download:', metaError.message);
          }

          return; // Success, exit early
        } else {
          console.log('Markdown API returned empty content, trying fallback...');
        }
      } catch (markdownError) {
        console.log('Markdown API failed, trying fallback...', markdownError.message);
      }

      // Fallback: Reports Direct API für interaktive Plots
      try {
        console.log('Attempting Direct API for Report...');
        const directResponse = await axios.get(directEndpoint, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          timeout: 8000
        });

        if (directResponse.data && directResponse.data.length > 0) {
          console.log('✓ Direct API successful');
          const latestReport = directResponse.data[0];
          const primaryFile = latestReport.files?.find(f => f.is_primary) || latestReport.files?.[0];

          if (primaryFile) {
            setReportMetadata({
              filename: primaryFile.file_name,
              size: `${Math.round(primaryFile.file_size / 1024)} KB`,
              creationDate: new Date(latestReport.generated_at).toLocaleDateString('de-DE'),
              reportId: latestReport.id,
              filePath: primaryFile.relative_path
            });
          }

          // Setze alle verfügbaren HTML-Dateien für Plots
          const allFiles = latestReport.files || [];
          const htmlFiles = allFiles.filter(f => f.file_type === 'html');
          const plotsData = htmlFiles.map(f => ({
            filename: f.file_name,
            url: `${API_BASE_URL}/hermes/reports/${reportId}/direct/download/${f.relative_path}`,
            type: f.file_type
          }));

          setAvailablePlots(plotsData);

          // Setze alle verfügbaren Dateien für Downloads Tab (außer Primary PDF)
          const nonPrimaryFiles = allFiles.filter(f => !f.is_primary).map(f => ({
            id: f.id,
            name: f.file_name,
            type: f.file_type,
            size: `${Math.round(f.file_size / 1024)} KB`,
            date: new Date(latestReport.generated_at).toLocaleDateString('de-DE'),
            description: f.file_type === 'html' ? 'Interaktive Visualisierung' :
                        f.file_type === 'pdf' ? 'PDF-Dokument' : 'Datendatei',
            downloadUrl: `${API_BASE_URL}/hermes/reports/${reportId}/direct/download/${f.relative_path}`
          }));

          // Kombiniere echte API-Dateien mit Mock-Dateien (wie in Provider App)
          const mockFiles = generateMockFiles(reportId);
          const combinedFiles = [...nonPrimaryFiles, ...mockFiles];
          setAvailableFiles(combinedFiles);

          // Erstelle Fallback-Markdown mit interaktiven Plots (wie Provider App)
          if (plotsData.length > 0) {
            const fallbackMarkdown = [{
              type: 'heading1',
              title: 'Studienergebnisse - Datenanalyse',
              content: [
                'Diese Visualisierungen zeigen die wichtigsten Erkenntnisse aus der durchgeführten Studie.',
                'Alle Diagramme sind interaktiv und bieten detaillierte Einblicke in die Datenanalyse.'
              ],
              plots: plotsData.map(plot => ({
                src: plot.url,
                altText: plot.filename.replace(/\.[^/.]+$/, "").replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                apiUrl: plot.url,
                filename: plot.filename,
                fileType: 'html'
              }))
            }];

            setMarkdownContent(fallbackMarkdown);
          } else {
            setMarkdownContent(null);
          }

          return; // Success with fallback
        }
      } catch (directError) {
        console.log('Direct API also failed:', directError.message);
      }

      // Both APIs failed - use mock data as final fallback
      console.warn('Both Markdown and Direct APIs failed, using mock data');
      const mockData = generateMockReportData(reportId, clientRequest.name);
      setMarkdownContent(mockData.content);
      setReportMetadata({
        size: '2.4 MB',
        creationDate: new Date().toLocaleDateString('de-DE'),
        pages: 45,
        reportId: reportId
      });
      setAvailablePlots(mockData.availablePlots || []);

    } catch (error) {
      console.error('Error in loadReportData:', error);
      setError(`Die Report-Daten konnten nicht geladen werden: ${error.message}`);
      setMarkdownContent(null);
      setReportMetadata(null);
      setAvailablePlots([]);
      setAvailableFiles([]);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSelectRequest = (request) => {
    setSelectedRequest(request);
    navigate(`/client/reports/${request.id}`);
    loadReportData(request);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleDownloadReport = () => {
    if (!selectedRequest || !reportMetadata) return;

    const token = localStorage.getItem('authToken');
    const reportId = mapClientIdToReportId(selectedRequest.id, selectedRequest.name);

    // Verwende Django Provider Direct API endpoint für Download (wie Provider App)
    const reportUrl = `${API_BASE_URL}/hermes/reports/${reportId}/direct/download/${reportMetadata.filePath}?token=${token}`;

    console.log(`Opening Django Provider report URL: ${reportUrl}`);

    // Öffne Report in neuem Tab
    window.open(reportUrl, '_blank');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'delivered':
        return <CheckCircleIcon color="success" />;
      default:
        return <AssignmentIcon color="primary" />;
    }
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Berichte und Ergebnisse
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Hier finden Sie alle abgeschlossenen Studien mit den zugehörigen Berichten und Ergebnissen.
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ borderRadius: 2 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="h2">
                  Abgeschlossene Studien
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    placeholder="Suchen..."
                    size="small"
                    variant="outlined"
                    sx={{ width: 200, mr: 2 }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                  />
                </Box>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : completedRequests.length > 0 ? (
                <TableContainer sx={{ maxHeight: '400px' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell width="50px"></TableCell>
                        <TableCell>Studientitel</TableCell>
                        <TableCell>Einrichtung</TableCell>
                        <TableCell>Datum</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {completedRequests
                        .filter(request => 
                          !searchTerm || 
                          (request.name || request.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (request.hospital || '').toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .slice(0, 5)
                        .map((request) => (
                          <TableRow 
                            key={request.id}
                            hover
                            selected={selectedRequest && selectedRequest.id === request.id}
                            onClick={() => handleSelectRequest(request)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                              '&.Mui-selected': { 
                                bgcolor: 'rgba(255, 122, 0, 0.08)',
                                '&:hover': { bgcolor: 'rgba(255, 122, 0, 0.12)' },
                              }
                            }}
                          >
                            <TableCell>
                              {getStatusIcon(request.status)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {request.name || request.title}
                              </Typography>
                            </TableCell>
                            <TableCell>{request.hospital || 'Unbekannte Einrichtung'}</TableCell>
                            <TableCell>{DateUtils.formatDate(request.completionDate || request.updatedAt || request.date)}</TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                label={request.status === 'completed' ? 'Abgeschlossen' : 'Übermittelt'} 
                                color="success" 
                                variant="outlined"
                                sx={{ height: 24, fontSize: '0.75rem' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Bericht anzeigen">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectRequest(request);
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Keine abgeschlossenen Studien gefunden.
                  </Typography>
                </Box>
              )}
              {completedRequests.length > 0 && (
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Klicken Sie auf eine Zeile, um den zugehörigen Bericht anzuzeigen
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            {selectedRequest ? (
              <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" component="h2">
                    {selectedRequest.name || selectedRequest.title}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ChevronLeftIcon />}
                    onClick={() => {
                      setSelectedRequest(null);
                      navigate('/client/reports');
                    }}
                  >
                    Zurück zur Übersicht
                  </Button>
                </Box>

                <Box sx={{ width: '100%', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab label="Studienbericht" />
                    <Tab label="Dateien & Downloads" />
                  </Tabs>
                </Box>

                {activeTab === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Studienbericht für: {selectedRequest.name || selectedRequest.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {selectedRequest.description || 'Keine Beschreibung verfügbar.'}
                      </Typography>
                      
                      {reportMetadata && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadReport}
                          >
                            PDF Report herunterladen
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {markdownLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : markdownContent ? (
                      // Markdown-Inhalt anzeigen
                      <>
                        {markdownContent.map((section, index) => (
                          <Card 
                            elevation={section.type === 'heading1' ? 2 : 1} 
                            sx={{ 
                              mb: 3,
                              bgcolor: section.type === 'heading1' ? '#f8f9fa' : 
                                      section.type === 'heading2' ? '#ffffff' : '#fafafa',
                              borderLeft: section.type === 'heading1' ? '4px solid #1976d2' : 
                                         section.type === 'heading2' ? '3px solid #42a5f5' : 
                                         '2px solid #90caf9'
                            }}
                            key={index}
                          >
                            <CardHeader 
                              title={
                                <Typography 
                                  variant={section.type === 'heading1' ? 'h5' : 
                                          section.type === 'heading2' ? 'h6' : 'subtitle1'}
                                  sx={{ 
                                    fontWeight: section.type === 'heading1' ? 'bold' : 
                                              section.type === 'heading2' ? 'medium' : 'normal' 
                                  }}
                                >
                                  {section.title}
                                </Typography>
                              }
                              // Keine Anzeige der Abschnittsarten mehr
                            />
                            <CardContent>
                              {section.content.length > 0 && (
                                <Box sx={{ 
                                  mb: 2, 
                                  pl: section.type === 'heading2' ? 2 : 
                                      section.type === 'heading3' ? 3 : 0,
                                  borderLeft: section.type === 'heading3' ? '1px solid #e0e0e0' : 'none'
                                }}>
                                  <div className="markdown-content" 
                                    dangerouslySetInnerHTML={{ 
                                      __html: section.content.join('\n')
                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                        .replace(/\*(.*?)\*\*/g, '<em>$1</em>')
                                        .replace(/^#### (.*?)$/gm, '<h4 style="margin-bottom: 0.2em; margin-top: 0.4em; font-size: 1.2em;"><strong>$1</strong></h4>')
                                        .replace(/^##### (.*?)$/gm, '<h5 style="margin-bottom: 0.2em; margin-top: 0.4em; font-size: 1.1em;"><strong>$1</strong></h5>')
                                        .replace(/\n/g, '<br />')
                                        .replace(/^- (.*)/gm, '• $1')
                                        .replace(/^(\d+)\. (.*)/gm, '$1. $2')
                                    }} 
                                  />
                                </Box>
                              )}
                              
                              {section.plots.length > 0 && (
                                <Grid container spacing={2}>
                                  {section.plots.map((plot, plotIndex) => {
                                    // Finde den entsprechenden Plot in availablePlots
                                    const matchingPlot = availablePlots.find(p => 
                                      p.filename.includes(plot.src.replace(/\.\.\//g, '').replace(/\.\//g, ''))
                                    );
                                    
                                    return (
                                      <Grid item xs={12} key={plotIndex}>
                                        <Box 
                                          sx={{ 
                                            border: '1px solid #eee', 
                                            borderRadius: 1, 
                                            p: 1, 
                                            height: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            mb: 2
                                          }}
                                        >
                                          {plot.apiUrl ? (
                                            <>
                                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                {plot.altText}
                                              </Typography>
                                              {(() => {
                                                // Erstelle HTML-Version des Pfades für interaktive Plots
                                                const htmlApiUrl = plot.apiUrl.replace('.svg', '.html');
                                                // Versuche zuerst die HTML-Version zu laden
                                                return (
                                                  <Box
                                                    component="iframe"
                                                    src={buildAuthenticatedUrl(htmlApiUrl)}
                                                    title={plot.altText}
                                                    sx={{ 
                                                      width: '100%', 
                                                      height: '600px', // Feste Standardhöhe
                                                      border: 'none',
                                                      margin: '0 auto',
                                                      display: 'block',
                                                      textAlign: 'center'
                                                    }}
                                                    onError={(e) => {
                                                      console.log("HTML-Version nicht verfügbar, versuche SVG:", htmlApiUrl);
                                                      // Wenn HTML-Version nicht verfügbar, zeige SVG
                                                      if (plot.filename && plot.filename.endsWith('.svg')) {
                                                        // SVG direkt anzeigen
                                                        const imgElement = document.createElement('img');
                                                        imgElement.src = buildAuthenticatedUrl(plot.apiUrl);
                                                        imgElement.alt = plot.altText;
                                                        imgElement.style.maxWidth = '100%';
                                                        imgElement.style.maxHeight = '700px';
                                                        imgElement.style.objectFit = 'contain';
                                                        
                                                        // Ersetze iframe mit img
                                                        e.target.parentNode.replaceChild(imgElement, e.target);
                                                        
                                                        // Fehlerbehandlung für das Bild
                                                        imgElement.onerror = () => {
                                                          console.error("Fehler beim Laden des SVG-Bildes:", plot.apiUrl);
                                                          imgElement.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+";
                                                          imgElement.style.padding = "50px";
                                                          imgElement.style.opacity = "0.5";
                                                        };
                                                      } else {
                                                        // Fallback für nicht-SVG-Dateien
                                                        e.target.style.display = 'none';
                                                        const errorMsg = document.createElement('div');
                                                        errorMsg.textContent = "Diagramm konnte nicht geladen werden";
                                                        errorMsg.style.color = '#666';
                                                        errorMsg.style.padding = '20px';
                                                        errorMsg.style.textAlign = 'center';
                                                        e.target.parentNode.appendChild(errorMsg);
                                                      }
                                                    }}
                                                  />
                                                );
                                              })()}
                                            </>
                                          ) : matchingPlot ? (
                                            <>
                                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                {plot.altText}
                                              </Typography>
                                              {matchingPlot.filename.endsWith('.svg') ? (
                                                <Box
                                                  component="img"
                                                  src={buildAuthenticatedUrl(matchingPlot.url)}
                                                  alt={plot.altText}
                                                  sx={{ 
                                                    maxWidth: '100%', 
                                                    maxHeight: 700,
                                                    objectFit: 'contain',
                                                    margin: '0 auto',
                                                    display: 'block'
                                                  }}
                                                />
                                              ) : (
                                                <Box
                                                  component="iframe"
                                                  src={buildAuthenticatedUrl(matchingPlot.url)}
                                                  title={plot.altText}
                                                  sx={{ 
                                                    width: '100%', 
                                                    height: '600px', // Feste Standardhöhe
                                                    border: 'none',
                                                    margin: '0 auto',
                                                    display: 'block'
                                                  }}
                                                />
                                              )}
                                            </>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">
                                              Plot nicht verfügbar: {plot.originalSrc || plot.src}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </>
                    ) : (
                      <Box sx={{ p: 3 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                          Für diese Studie ist kein Markdown-Bericht verfügbar. Sie können den PDF-Bericht herunterladen, falls verfügbar.
                        </Alert>
                        
                        {reportMetadata && (
                          <Card elevation={2} sx={{ mt: 4, p: 3, textAlign: 'center' }}>
                            <PdfIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                              PDF-Report verfügbar
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              Größe: {reportMetadata.size} • Erstellt am: {reportMetadata.creationDate}
                            </Typography>
                            <Button 
                              variant="contained" 
                              color="primary" 
                              startIcon={<DownloadIcon />}
                              onClick={handleDownloadReport}
                              sx={{ mt: 2 }}
                            >
                              PDF Report herunterladen
                            </Button>
                          </Card>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Verfügbare Dateien
                    </Typography>
                    
                    {reportLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Datei</TableCell>
                              <TableCell>Beschreibung</TableCell>
                              <TableCell>Größe</TableCell>
                              <TableCell>Datum</TableCell>
                              <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Hauptbericht (PDF) */}
                            {reportMetadata && (
                              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PdfIcon color="error" />
                                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                                      Hauptbericht (PDF)
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>Finaler Report mit allen Studienergebnissen und Analysen</TableCell>
                                <TableCell>{reportMetadata.size}</TableCell>
                                <TableCell>{reportMetadata.creationDate}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Herunterladen">
                                    <IconButton size="small" onClick={handleDownloadReport} color="primary">
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {/* Weitere Dateien aus der Django Provider API */}
                            {availableFiles.map((file) => (
                              <TableRow key={file.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {file.type === 'pdf' ? (
                                      <PdfIcon color="error" />
                                    ) : (
                                      <FileIcon color="primary" />
                                    )}
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                      {file.name}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{file.description}</TableCell>
                                <TableCell>{file.size}</TableCell>
                                <TableCell>{file.date}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Herunterladen">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        const token = localStorage.getItem('authToken');
                                        const downloadUrl = `${file.downloadUrl}?token=${token}`;
                                        window.open(downloadUrl, '_blank');
                                      }}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                            
                            {/* Wenn keine Dateien verfügbar sind */}
                            {(!reportMetadata && availableFiles.length === 0) && (
                              <TableRow>
                                <TableCell colSpan={5} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    Keine Dateien verfügbar
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    
                    {selectedRequest.results && selectedRequest.results.keyFindings && selectedRequest.results.keyFindings.length > 0 && (
                      <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          Wichtigste Ergebnisse
                        </Typography>
                        <List>
                          {selectedRequest.results.keyFindings.map((finding, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <CheckCircleIcon color="success" />
                              </ListItemIcon>
                              <ListItemText primary={finding} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            ) : (
              <Paper elevation={2} sx={{ p: 4, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7, mb: 2 }} />
                <Typography variant="h6" align="center" gutterBottom>
                  Wählen Sie eine Studie aus
                </Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                  Bitte wählen Sie eine abgeschlossene Studie aus der Liste links, um den zugehörigen Bericht anzuzeigen.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default ReportsPage;
